// 千日手の軍配者たち (src/twins_fight.ts) の仮の絵 (2026-10-29)。描いた絵が来るまでの図形。形と色はラボ (src/twins_proxy_lab.ts) と同じ。
//   合図は殴る側の体に出す: 避ける技 (赤) は体が赤く光る・弾く技は得物 (軍配・鈴) が動く。札や文字はゲームでは出さない。
//   2026-10-30 から本番の絵: 二人の立ち絵 (`public/sprites/bosses/twins/{ane,imo}_idle.png`・右向き) と横長の装置 (`device_wide.png`)。
//   装置の大きさは作者 c2「画面いっぱいはでかすぎるけど割と近い／横550pxよりは大きい／もう少し横長の敵に」で横 760px に決めた。
//   攻撃の姿勢の絵が来るまでは、立ち絵を傾けて (姉) と震わせて (妹) 合図にする。絵が読めない所 (テスト) では前の図形へ戻る。
import type { GameState } from './types'
import { ARENA, arenaBoundsFor } from './balance'
import { REVEAL, curtainSpan, devRedSpan, hopLift, twinGlowsRed, mapEdgeX, mapSpan, twinsBroken, twinsDeviceShown, twinsPrepare, twinsRevealLook, type DevAtk, type TwinState, type TwinsFight } from './twins_fight'
import { drawDropBehind, drawDropFront } from './twins_reveal_drop'
import type { TwinId } from './twins_proxy_plan'
import { drawLampMote, lampMoteOld } from './lamp_mote'
import { UI_SERIF } from './ui_font'

const TWIN_H = 150
const FLOOR = ARENA.floorY

function shadow(ctx: CanvasRenderingContext2D, x: number, w: number, a: number): void {
  ctx.fillStyle = `rgba(0,0,0,${a})`
  ctx.beginPath(); ctx.ellipse(x, FLOOR + 4, w / 2, 8, 0, 0, Math.PI * 2); ctx.fill()
}

const DEVICE_SRC = '/sprites/bosses/twins/device_wide.png'
const DEVICE_ASPECT = 381 / 1200
/** 絵の中の目 (吊り下げた照明の青いレンズ) の位置。切り抜いた絵の幅・高さに対する割合 (色で測った・10-30)。 */
const DEVICE_EYES: [number, number][] = [[0.36, 0.356], [0.642, 0.356]]
/** 装置の横幅と、下端の床からの高さ。 */
export const DEVICE_W = 760
const DEVICE_LIFT = 30
const TWIN_SRC = { ane: '/sprites/bosses/twins/ane_idle.png', imo: '/sprites/bosses/twins/imo_idle.png' } as const
/** 立ち絵の画面での高さ (頭巾の上から裾まで)。 */
const TWIN_SPR_H = 175
/** 立ち絵の絵の高さ (px)。姿勢の絵は立ち絵と同じ縮尺で切り出してあるので、どれもこの割合で縮める。 */
const TWIN_SRC_H = 340
/** 技ごとの [合図の姿勢, 振った後の姿勢] (10-30)。絵がまだ無い姿勢は立ち絵で描く。 */
const TWIN_POSES: Record<string, Partial<Record<TwinId, [string, string]>>> = {
  a_strike: { ane: ['raise', 'chop'] },
  a_double: { ane: ['sweep', 'chop'] },
  a_lunge: { ane: ['coil', 'thrust'] },
  a_order: { ane: ['raise', 'raise'] },
  j_sennichite: { ane: ['sweep', 'chop'], imo: ['raise', 'strike'] },
  i_bell: { imo: ['ring', 'ring'] },
  i_strike: { imo: ['raise', 'strike'] },
  i_triple: { imo: ['hug', 'strike'] },
  i_slide: { imo: ['low', 'low'] },
}
/** 足元 (裾の下端の帯) の中心の、絵の幅に対する割合。絵ごとに測った値 (10-30)。 */
const POSE_FOOT: Record<string, number> = {
  ane_raise: 0.52, ane_chop: 0.449, ane_sweep: 0.526, ane_coil: 0.479, ane_thrust: 0.406,
  imo_ring: 0.507, imo_raise: 0.513, imo_strike: 0.474, imo_hug: 0.486, imo_low: 0.491,
  ane_down: 0.45, imo_down: 0.5,
}
/** 振った後の姿勢を見せておく長さ。 */
const RELEASE_MS = 300
const imgs = new Map<string, HTMLImageElement>()
function image(src: string): HTMLImageElement | null {
  if (typeof Image === 'undefined') return null
  let im = imgs.get(src)
  if (!im) { im = new Image(); im.src = src; imgs.set(src, im) }
  return im.complete && im.naturalWidth > 0 ? im : null
}
function deviceImage(): HTMLImageElement | null { return image(DEVICE_SRC) }

export function deviceRect(): { x0: number; x1: number; top: number; bottom: number } {
  const w = arenaBoundsFor('hollow-actor')
  const mid = (w.leftWall + w.rightWall) / 2
  const bottom = FLOOR - DEVICE_LIFT
  return { x0: mid - DEVICE_W / 2, x1: mid + DEVICE_W / 2, top: bottom - DEVICE_W * DEVICE_ASPECT, bottom }
}

function eyes(): [number, number][] {
  const r = deviceRect(), w = r.x1 - r.x0, h = r.bottom - r.top
  return DEVICE_EYES.map(([fx, fy]) => [r.x0 + w * fx, r.top + h * fy])
}

function drawDevice(ctx: CanvasRenderingContext2D, active: boolean, d: DevAtk | null, nowMs: number): void {
  const r = deviceRect(), w = r.x1 - r.x0, h = r.bottom - r.top, s = w / 560
  ctx.save()
  ctx.globalAlpha = active ? 0.95 : 0.5
  const img = deviceImage()
  if (img) {
    ctx.drawImage(img, r.x0, r.top, w, h)
    ctx.restore()
    return
  }
  ctx.fillStyle = '#25242b'; ctx.fillRect(r.x0, r.top + 30, 60 * s, h - 30); ctx.fillRect(r.x1 - 60 * s, r.top + 30, 60 * s, h - 30)
  ctx.fillStyle = '#3a3830'; ctx.fillRect(r.x0 + 80 * s, r.top + 50, w - 160 * s, h - 60)
  ctx.strokeStyle = '#5a564a'; ctx.lineWidth = 1
  for (let i = 1; i < 9; i++) {
    const x = r.x0 + 80 * s + (w - 160 * s) * i / 9, y = r.top + 50 + (h - 60) * i / 9
    ctx.beginPath(); ctx.moveTo(x, r.top + 50); ctx.lineTo(x, r.bottom - 10); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(r.x0 + 80 * s, y); ctx.lineTo(r.x1 - 80 * s, y); ctx.stroke()
  }
  const mid = (r.x0 + r.x1) / 2
  ctx.fillStyle = '#2c2b30'; ctx.fillRect(mid - 70 * s, r.top + 60, 140 * s, 60)
  const lit = !!(d && (d.kind === 'light' || nowMs - d.start < 500))
  for (const [x, y] of eyes()) {
    ctx.fillStyle = lit && active ? '#fff6d8' : '#8a8578'
    ctx.beginPath(); ctx.arc(x, y, 9 * Math.min(1.4, s), 0, Math.PI * 2); ctx.fill()
  }
  ctx.restore()
}

function drawDevAtk(ctx: CanvasRenderingContext2D, d: DevAtk, nowMs: number): void {
  const b = arenaBoundsFor('hollow-actor')
  const w = { left: b.leftWall + 40, right: b.rightWall - 40, mid: (b.leftWall + b.rightWall) / 2 }
  const el = nowMs - d.start
  // 弾けない床の技は、叩く床を赤く光らせて予告する (赤＝弾けない・ガードできない、の決まりにそろえる・11-03)。
  const red = devRedSpan(d, w, nowMs)
  if (red) {
    const k = Math.min(1, el / 400)
    ctx.save(); ctx.shadowColor = '#ff2a2a'; ctx.shadowBlur = 22
    ctx.fillStyle = `rgba(255,42,42,${0.18 + 0.32 * k})`; ctx.fillRect(red[0], FLOOR - 6, red[1] - red[0], 10)
    ctx.restore()
  }
  if (d.kind === 'march') {
    const edge = d.side > 0 ? w.right + 40 : w.left - 40
    if (el < 900) for (let i = 0; i < 3; i++) shadow(ctx, edge - d.side * (i * 70 + 30), 70, 0.15 + 0.5 * el / 900)
    // 駒そのものは当たり (travel) の位置に描く＝drawTwinsFight の march 駒の描画で。ここでは予告だけ。
  }
  if (d.kind === 'wall') {
    const fall = Math.min(1, Math.max(0, (el - 1000) / 120))
    shadow(ctx, d.x, el < 1120 ? 60 + 50 * Math.min(1, el / 1000) : 110, el < 1120 ? 0.2 + 0.5 * Math.min(1, el / 1000) : 0.6)
    if (d.wallHp > 0 && nowMs < d.wallUntil) {
      const y = el < 1000 ? -60 : -60 + (FLOOR + 60) * fall
      ctx.fillStyle = '#121216'; ctx.fillRect(d.x - 38, y - 230, 76, 230)
      ctx.strokeStyle = '#555'; ctx.strokeRect(d.x - 38, y - 230, 76, 230)
      ctx.fillStyle = '#cfc8b2'; ctx.font = `bold 30px ${UI_SERIF}`; ctx.textAlign = 'center'; ctx.fillText('金', d.x, y - 120)
      if (el >= 1120) { ctx.fillStyle = `rgba(255,238,150,${0.25 + 0.15 * d.wallHp})`; ctx.fillRect(d.x - 40, FLOOR - 232, 80, 4) }
    }
  }
  if (d.kind === 'map') {
    const [x0, x1] = mapSpan(d, w)
    const k = Math.min(1, el / 1300)
    ctx.fillStyle = `rgba(0,0,0,${0.15 + 0.55 * k})`; ctx.fillRect(x0, FLOOR - 4, x1 - x0, 30)
    const ang = el < 1300 ? (1 - Math.pow(k, 3)) * Math.PI / 2 : 0
    const h = el < 3500 ? 200 * Math.sin(ang) + 20 : 0
    if (el < 3500) {
      ctx.fillStyle = '#5b5040'; ctx.fillRect(x0 + 10, FLOOR - h, x1 - x0 - 20, Math.max(14, h))
      ctx.strokeStyle = '#a99c78'; ctx.strokeRect(x0 + 10, FLOOR - h, x1 - x0 - 20, Math.max(14, h))
      if (el >= 1300) { const e = mapEdgeX(d, w); ctx.fillStyle = '#fe9'; ctx.fillRect(e - 6, FLOOR - 26, 12, 26) }
    }
  }
  if (d.kind === 'curtain') {
    const [x0, x1] = curtainSpan(d, w)
    const top = FLOOR - 520
    let bottom: number
    if (el < 1400) bottom = top + 40 + 60 * el / 1400
    else if (el < 1600) bottom = top + 100 + (FLOOR - top - 100) * (el - 1400) / 200
    else bottom = FLOOR - (FLOOR - top + 40) * Math.min(1, (el - 1700) / 900)
    const k = Math.min(1, el / 1400)
    ctx.fillStyle = `rgba(0,0,0,${el < 1600 ? 0.15 + 0.5 * k : 0.6})`; ctx.fillRect(x0, FLOOR - 4, x1 - x0, 26)
    if (bottom > top) {
      for (let x = x0; x < x1; x += 30) { ctx.fillStyle = ((x - x0) / 30) % 2 ? '#c9c6c0' : '#151518'; ctx.fillRect(x, top, Math.min(30, x1 - x), bottom - top) }
      ctx.fillStyle = '#0d0d0f'; ctx.fillRect(x0 - 6, top, x1 - x0 + 12, 10)
    }
  }
  if (d.kind === 'light') {
    const fadeIn = Math.min(1, el / 250), stopped = el >= 1600
    const a = fadeIn * (el > 2600 ? Math.max(0, 1 - (el - 2600) / 500) : 1)
    const rx = 66, cx = d.lightX, cy = FLOOR + 2
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    for (const [ex, ey] of eyes()) {
      const warm = stopped ? '255,236,190' : '210,220,255'
      const g = ctx.createLinearGradient(ex, ey, cx, cy)
      g.addColorStop(0, `rgba(${warm},${0.42 * a})`); g.addColorStop(1, `rgba(${warm},${0.2 * a})`)
      ctx.fillStyle = g
      ctx.beginPath(); ctx.moveTo(ex - 4, ey); ctx.lineTo(ex + 4, ey); ctx.lineTo(cx + rx, cy); ctx.lineTo(cx - rx, cy); ctx.closePath(); ctx.fill()
    }
    ctx.fillStyle = `rgba(255,245,215,${0.6 * a})`
    ctx.beginPath(); ctx.ellipse(cx, cy, rx, 13, 0, 0, Math.PI * 2); ctx.fill()
    ctx.restore()
    if (el >= 2050 && el < 3000) {
      const y = Math.min(FLOOR, FLOOR - 520 + 520 * Math.min(1, (el - 2000) / 100))
      ctx.fillStyle = '#d8d2c0'; ctx.fillRect(cx - 28, y - 86, 56, 86)
      ctx.fillStyle = '#222'; ctx.font = `bold 24px ${UI_SERIF}`; ctx.textAlign = 'center'; ctx.fillText('歩', cx, y - 36)
    }
  }
}

function drawTwin(ctx: CanvasRenderingContext2D, t: TwinState, nowMs: number, broken: boolean): void {
  const pale = t.id === 'ane'
  const body = pale ? '#c9c9cf' : '#1b1b20', line = pale ? '#88888f' : '#8d8d99'
  if (t.down) {
    const id = t.id === 'ane' ? 'ane' : 'imo'
    const fallen = image(`/sprites/bosses/twins/${id}_down.png`)
    if (fallen) {
      // 倒れた姿 (姉＝膝をついて前へ折れる・妹＝横座りで崩れる。得物は床に落ちている)。立ち絵と同じ縮尺。
      const k = TWIN_SPR_H / TWIN_SRC_H, w = fallen.naturalWidth * k, h = fallen.naturalHeight * k
      shadow(ctx, t.x, 90, 0.4)
      ctx.save(); ctx.translate(t.x, FLOOR + 4); ctx.scale(t.face < 0 ? -1 : 1, 1)
      ctx.drawImage(fallen, -w * (POSE_FOOT[`${id}_down`] ?? 0.5), -h, w, h); ctx.restore()
      return
    }
    const spr = image(TWIN_SRC[id])
    if (spr) {
      // 倒れた姿の絵が来るまでは、立ち絵を床へ寝かせる (頭が向いていた側へ)。
      const h = TWIN_SPR_H, w = h * spr.naturalWidth / spr.naturalHeight
      ctx.save(); ctx.translate(t.x, FLOOR - w / 2 + 6); ctx.rotate(t.face * Math.PI / 2); ctx.globalAlpha = 0.85
      ctx.drawImage(spr, -w / 2, -h / 2, w, h); ctx.restore()
      return
    }
    ctx.fillStyle = body; ctx.fillRect(t.x - 70, FLOOR - 34, 140, 34); ctx.strokeStyle = line; ctx.strokeRect(t.x - 70, FLOOR - 34, 140, 34)
    return
  }
  const m = t.move, el = m ? nowMs - t.start : -1
  const next = m ? m.hits.map((x, i) => ({ x, i })).find(({ x, i }) => x.by === t.id && !t.done.has(i)) : undefined
  const winding = !!(next && el >= 0 && el < next.x.at)
  // 赤く光るのは、赤い当たりの前の決まった間だけ (合わせ技では相方の弾く当たりの後から・src/twins_proxy_plan.ts redGlowFrom・11-03)。
  const red = twinGlowsRed(t, nowMs)
  const crouch = (m?.slide && winding ? 40 : 0) + (broken ? 30 : 0)
  // 跳び越えの間は体だけ持ち上げる (影は床に残し、高いほど小さく薄く)。
  const lift = hopLift(t, nowMs)
  shadow(ctx, t.x, 70 * (1 - lift / 400), 0.4 * (1 - lift / 300))
  const idle = image(TWIN_SRC[t.id === 'ane' ? 'ane' : 'imo'])
  if (idle) {
    const pose = poseName(t, el)
    const spr = (pose && image(`/sprites/bosses/twins/${t.id}_${pose}.png`)) || idle
    ctx.save(); ctx.translate(0, -lift)
    drawTwinSprite(ctx, t, spr, spr === idle ? null : `${t.id}_${pose}`, { el, winding, red, crouch, at: next?.x.at ?? 1 }, nowMs)
    ctx.restore()
    return
  }
  ctx.save()
  if (red) { ctx.shadowColor = '#ff2a2a'; ctx.shadowBlur = 26 }
  ctx.fillStyle = body; ctx.fillRect(t.x - 22, FLOOR - TWIN_H + crouch, 44, TWIN_H - crouch)
  ctx.restore()
  ctx.strokeStyle = line; ctx.strokeRect(t.x - 22, FLOOR - TWIN_H + crouch, 44, TWIN_H - crouch)
  // 黒子の頭巾
  ctx.fillStyle = '#0d0d10'; ctx.fillRect(t.x - 18, FLOOR - TWIN_H - 30 + crouch, 36, 34)
  // 得物: 姉＝軍配 (合図で上げる)、妹＝鈴 (合図で揺れる)
  const handX = t.x + t.face * 26
  if (pale) {
    const up = winding ? Math.min(1, el / Math.max(1, next!.x.at - 120)) : 0
    const hy = FLOOR - 90 - up * 90 + crouch
    ctx.strokeStyle = '#ddd'; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(handX, FLOOR - 80 + crouch); ctx.lineTo(handX, hy); ctx.stroke(); ctx.lineWidth = 1
    ctx.fillStyle = '#e9e4d6'; ctx.beginPath(); ctx.ellipse(handX, hy - 14, 14, 18, 0, 0, Math.PI * 2); ctx.fill()
  } else {
    const shake = winding ? Math.sin(nowMs / 30) * 6 : 0
    ctx.fillStyle = '#c8b46a'; ctx.beginPath(); ctx.arc(handX + shake, FLOOR - 82 + crouch, 9, 0, Math.PI * 2); ctx.fill()
  }
  // 残りの体力 (1人ずつ)。
  const k = Math.max(0, 1 - t.lost / Math.max(1, t.pool))
  ctx.fillStyle = '#000a'; ctx.fillRect(t.x - 30, FLOOR - TWIN_H - 44 + crouch, 60, 5)
  ctx.fillStyle = pale ? '#e0e0e8' : '#9a9aff'; ctx.fillRect(t.x - 30, FLOOR - TWIN_H - 44 + crouch, 60 * k, 5)
}

/** 技の途中で見せる姿勢の名前。合図の間は合図の姿勢、自分の一打の直後は振った後の姿勢。それ以外は null (立ち絵)。 */
export function poseName(t: TwinState, el: number): string | null {
  const m = t.move
  const p = m && TWIN_POSES[m.id]?.[t.id]
  if (!m || !p || el < 0) return null
  const mine = m.hits.filter((x) => x.by === t.id)
  if (mine.some((x) => el >= x.at && el < x.at + RELEASE_MS)) return p[1]
  return mine.some((x) => el < x.at) ? p[0] : null
}

/** 立ち絵で描く。絵は右向きなので、左を向く時は裏返す。pose が null なら立ち絵 (姿勢の絵がまだ無い時も)。 */
function drawTwinSprite(ctx: CanvasRenderingContext2D, t: TwinState, spr: HTMLImageElement, pose: string | null, a: { el: number; winding: boolean; red: boolean; crouch: number; at: number }, nowMs: number): void {
  const k = TWIN_SPR_H / TWIN_SRC_H
  const w = spr.naturalWidth * k, h = spr.naturalHeight * k * (TWIN_SPR_H - a.crouch) / TWIN_SPR_H
  const foot = pose ? POSE_FOOT[pose] ?? 0.5 : 0.5
  // 姿勢の絵が無い時の合図: 姉は体を後ろへ反らす・妹は小刻みに震える。
  const up = !pose && t.id === 'ane' && a.winding ? Math.min(1, a.el / Math.max(1, a.at - 120)) : 0
  const shake = !pose && t.id !== 'ane' && a.winding ? Math.sin(nowMs / 30) * 3 : 0
  ctx.save()
  ctx.translate(t.x + shake, FLOOR + 4)
  ctx.scale(t.face < 0 ? -1 : 1, 1)
  ctx.rotate(-0.12 * up)
  if (a.red) { ctx.shadowColor = '#ff2a2a'; ctx.shadowBlur = 26 }
  ctx.drawImage(spr, -w * foot, -h, w, h)
  ctx.restore()
  const life = Math.max(0, 1 - t.lost / Math.max(1, t.pool))
  const y = FLOOR - TWIN_SPR_H + a.crouch - 14
  ctx.fillStyle = '#000a'; ctx.fillRect(t.x - 30, y, 60, 5)
  ctx.fillStyle = t.id === 'ane' ? '#e0e0e8' : '#9a9aff'; ctx.fillRect(t.x - 30, y, 60 * life, 5)
}

/** 行軍の駒 (travel の当たり) の絵。弾いて返した駒も同じ形。 */
function drawMarchPieces(ctx: CanvasRenderingContext2D, state: GameState): void {
  for (const h of state.hits) {
    if (h.attackId !== 'twins_dev_march' || h.x === undefined || (h.consumed && !h.reflected)) continue
    const x = h.x
    shadow(ctx, x, 80, 0.55)
    ctx.fillStyle = '#d8d2c0'; ctx.beginPath()
    ctx.moveTo(x - 32, FLOOR); ctx.lineTo(x + 32, FLOOR); ctx.lineTo(x + 24, FLOOR - 80); ctx.lineTo(x, FLOOR - 98); ctx.lineTo(x - 24, FLOOR - 80); ctx.closePath(); ctx.fill()
    ctx.fillStyle = '#222'; ctx.font = `bold 26px ${UI_SERIF}`; ctx.textAlign = 'center'; ctx.fillText('歩', x, FLOOR - 40)
  }
  for (const h of state.hits) {
    if (h.follow !== 'travel' || h.attackId === 'twins_dev_march' || !h.attackId.startsWith('twins_') || h.x === undefined || (h.consumed && !h.reflected)) continue
    ctx.fillStyle = h.reflected ? '#ffe9a0' : '#e6d48a'
    ctx.beginPath(); ctx.arc(h.x, h.y ?? FLOOR - 90, 9, 0, Math.PI * 2); ctx.fill()
  }
}

// ---- 3部屋目の現れ (10-30 作者「2幕のちめいをいれてから光を浮上させた後、画面中央に光が集中して突如として3幕目が現れる」) ----
//   光は常夜燈の青 (#aacdff) より白い青白。線や輪は使わず、ぼかした粒と塊だけで描く。
const REVEAL_MOTES = 16
const clamp01 = (v: number) => Math.max(0, Math.min(1, v))
/** 決まった乱数 (毎コマ同じ粒を同じ所に出す)。 */
function hash(n: number): number { const x = Math.sin(n * 127.1 + 311.7) * 43758.5453; return x - Math.floor(x) }
function glow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, a: number): void {
  if (a <= 0.003 || r <= 0.5) return
  const g = ctx.createRadialGradient(x, y, 0, x, y, r)
  g.addColorStop(0, `rgba(240,248,255,${a})`)
  g.addColorStop(0.35, `rgba(196,222,255,${a * 0.55})`)
  g.addColorStop(1, 'rgba(170,205,255,0)')
  ctx.fillStyle = g
  ctx.fillRect(x - r, y - r, r * 2, r * 2)
}
/** 光が集まる所 (装置の二つの目の間)。 */
export function revealCenter(): { x: number; y: number } {
  const [[x0, y0], [x1, y1]] = eyes() as [[number, number], [number, number]]
  return { x: (x0 + x1) / 2, y: (y0 + y1) / 2 }
}
/** 粒の位置と濃さ。e = 現れの始まりからの ms。見えない時は null。 */
export function revealMote(e: number, bodyX: number, k: number, c: { x: number; y: number }): { x: number; y: number; a: number; r: number } | null {
  const delay = hash(k) * 520, dx = (hash(k + 0.5) - 0.5) * 90, sy = FLOOR - 20 - hash(k + 0.25) * 60
  const riseH = 70 + hash(k + 0.75) * 110
  const t0 = REVEAL.rise + delay
  if (e < t0 || e >= REVEAL.appear) return null
  const u = clamp01((e - t0) / (REVEAL.gather - REVEAL.rise))
  const ease = 1 - (1 - u) * (1 - u)
  const rx = bodyX + dx + Math.sin(e / 260 + k * 1.7) * 10 * ease, ry = sy - riseH * ease
  const v = clamp01((e - REVEAL.gather) / (REVEAL.appear - REVEAL.gather))
  const pull = v * v * v
  return { x: rx + (c.x - rx) * pull, y: ry + (c.y - ry) * pull, a: Math.min(1, (e - t0) / 220) * (0.55 + 0.45 * pull), r: 9 + hash(k + 0.1) * 7 - pull * 4 }
}
function fallenTwin(id: TwinId, x: number, face: 1 | -1): TwinState {
  return { id, x, face, pool: 1, lost: 1, down: true, downUntil: 0, move: null, start: 0, booked: [], done: new Set(), nextThink: 0, fromX: x, lunge: 0, crossing: false, told: false, hop: null }
}
/** 比較版 C (上から落ちる重さ・src/twins_reveal_drop.ts) の装置の後ろ。装置より先に描く。 */
function drawDropBehindOf(ctx: CanvasRenderingContext2D, f: TwinsFight, nowMs: number): void {
  const rv = f.reveal
  if (!rv || twinsRevealLook() !== 'drop') return
  drawDropBehind(ctx, nowMs - rv.start, deviceRect(), FLOOR, rv.bodies,
    (alpha) => {
      ctx.save(); ctx.globalAlpha = alpha
      for (const b of rv.bodies) drawTwin(ctx, fallenTwin(b.id, b.x, b.face), nowMs, false)
      ctx.restore()
    },
    (dy, stretch) => {
      const r = deviceRect()
      ctx.save(); ctx.translate(0, r.bottom + dy); ctx.scale(1, stretch); ctx.translate(0, -r.bottom)
      drawDevice(ctx, true, null, nowMs)
      ctx.restore()
    })
}
function drawRevealBefore(ctx: CanvasRenderingContext2D, f: TwinsFight, nowMs: number): void {
  const rv = f.reveal
  if (!rv || twinsRevealLook() !== 'light') return
  const e = nowMs - rv.start
  if (e >= REVEAL.appear) return
  // 倒れた二人: 光が浮かぶにつれて薄れて消える。
  const fade = 1 - clamp01((e - REVEAL.rise) / (REVEAL.gather - REVEAL.rise - 200))
  if (fade > 0) {
    ctx.save(); ctx.globalAlpha = fade
    for (const b of rv.bodies) {
      drawTwin(ctx, fallenTwin(b.id, b.x, b.face), nowMs, false)
    }
    ctx.restore()
  }
  const c = revealCenter()
  ctx.save(); ctx.globalCompositeOperation = 'lighter'
  rv.bodies.forEach((b, bi) => {
    // 体の上に残る薄い光 (粒が抜けていく元)。
    glow(ctx, b.x, FLOOR - 50, 80, 0.22 * clamp01((e - REVEAL.rise) / 400) * fade)
    for (let i = 0; i < REVEAL_MOTES; i++) {
      const m = revealMote(e, b.x, bi * 100 + i, c)
      // 1粒は常夜燈の光の正本 (src/lamp_mote.ts・10-31)。前の版 (白っぽいにじみ・半径 22〜38) = DEV ?lampMote=old。
      if (m) { if (lampMoteOld()) glow(ctx, m.x, m.y, m.r * 2.4, m.a * 0.8); else drawLampMote(ctx, m.x, m.y, m.a) }
    }
  })
  // 中央に集まる光の塊。集まるほど強くなる。
  const v = clamp01((e - REVEAL.gather) / (REVEAL.appear - REVEAL.gather))
  glow(ctx, c.x, c.y, 30 + 90 * v * v, 0.25 + 0.75 * v)
  ctx.restore()
}
function drawRevealAfter(ctx: CanvasRenderingContext2D, f: TwinsFight, nowMs: number): void {
  const rv = f.reveal
  if (!rv) return
  if (twinsRevealLook() === 'drop') { drawDropFront(ctx, nowMs - rv.start, deviceRect(), eyes()); return }
  const e = nowMs - rv.start
  if (e < REVEAL.appear || e >= REVEAL.end) return
  // 装置が出た一瞬、集めた光が弾けて部屋を明るくし、すぐ引く。
  const w = 1 - clamp01((e - REVEAL.appear) / (REVEAL.end - REVEAL.appear))
  const c = revealCenter()
  ctx.save(); ctx.globalCompositeOperation = 'lighter'
  glow(ctx, c.x, c.y, 160 + 520 * (1 - w), w * w)
  ctx.fillStyle = `rgba(190,215,255,${0.3 * w * w * w})`
  ctx.fillRect(c.x - 2000, c.y - 2000, 4000, 4000)
  ctx.restore()
}

/** drawHollowActor の代わり。描いたら true。 */
export function drawTwinsFight(ctx: CanvasRenderingContext2D, state: GameState): boolean {
  const f = twinsPrepare(state.boss, state.player, state.time)
  const nowMs = state.time * 1000
  ctx.save()
  drawDropBehindOf(ctx, f, nowMs)
  // 装置は3部屋目に現れてから描く (1・2部屋目は画面のどこにも出さない・10-30 作者「3幕の姿は3幕になるまで出さないで」)。
  if (twinsDeviceShown(f, nowMs)) {
    drawDevice(ctx, f.devLost < f.devPool, f.dev, nowMs)
    if (f.dev) drawDevAtk(ctx, f.dev, nowMs)
  }
  drawRevealBefore(ctx, f, nowMs)
  const broken = twinsBroken(state.time)
  // 赤の合図の方を手前に描く (重なった時に見える)。
  for (const t of [...f.twins].sort((a, b) => Number(!!a.move) - Number(!!b.move))) drawTwin(ctx, t, nowMs, broken)
  drawMarchPieces(ctx, state)
  drawRevealAfter(ctx, f, nowMs)
  ctx.restore()
  return true
}
